import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api, setAuthToken, getAuthToken } from '../services/api';
import { transformationService, TransformationItem } from '../services/transformationService';
import { contentService, SiteContent } from '../services/contentService';
import ImageUploadComponent from './ImageUploadComponent';

interface UploadedFile {
  original: string;
  thumbnail: string;
  filename: string;
  size: number;
}

interface GalleryItem {
  id: number;
  title: string;
  description: string;
  category?: string;
  location?: string;
  images: string[];
  thumbnails?: string[];
  createdAt: string;
  updatedAt: string;
}

const AdminPanel: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ login: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'gallery' | 'transformations' | 'content' | 'users' | 'settings' | 'analytics'>('gallery');

  // Gallery state
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [editingGalleryItem, setEditingGalleryItem] = useState<GalleryItem | null>(null);
  const [galleryFormData, setGalleryFormData] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    images: [] as string[],
  });

  // Transformations state
  const [transformations, setTransformations] = useState<TransformationItem[]>([]);
  const [editingTransformation, setEditingTransformation] = useState<TransformationItem | null>(null);
  const [transformationFormData, setTransformationFormData] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    images: [] as string[],
    beforeImageIndex: 0,
    afterImageIndex: 1,
  });

  // Content state
  const [siteContent, setSiteContent] = useState<SiteContent | null>(null);
  const [editingContent, setEditingContent] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();
      if (token) {
        try {
          await api.checkAuth();
          setIsAuthenticated(true);
        } catch (err) {
          setAuthToken(null);
          setIsAuthenticated(false);
        }
      }
    };

    // Zawsze ładuj dane (galeria jest publiczna)
    loadData();
    checkAuth();
  }, []);

  const loadData = async () => {
    try {
      const [galleryResponse, transformationsData, contentData] = await Promise.all([
        api.getGallery(),
        transformationService.getTransformations(),
        contentService.getContent()
      ]);

      setGalleryItems(galleryResponse.data);
      setTransformations(transformationsData);
      setSiteContent(contentData);
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await api.login(loginData);
      setAuthToken(response.data.token);
      setIsAuthenticated(true);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd logowania');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    setAuthToken(null);
    setIsAuthenticated(false);
    setGalleryItems([]);
    setTransformations([]);
    setSiteContent(null);
  };

  const handleFileUpload = async (files: FileList) => {
    if (files.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('images', file);
      });

      const response = await api.uploadImages(formData);
      const uploadedFiles: UploadedFile[] = response.data.files;

      setSuccess(`Przesłano ${uploadedFiles.length} plików pomyślnie!`);
      
      // Display uploaded file URLs for easy copying
      uploadedFiles.forEach(file => {
        console.log(`Uploaded: ${file.original}`);
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd przesyłania plików');
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryFileUpload = async (files: FileList) => {
    if (files.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('images', file);
      });

      const response = await api.uploadImages(formData);
      const uploadedFiles: UploadedFile[] = response.data.files;

      // Dodaj przesłane zdjęcia do galleryFormData.images
      const newImages = uploadedFiles.map(file => file.original);
      setGalleryFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages]
      }));

      setSuccess(`Przesłano ${uploadedFiles.length} plików do galerii pomyślnie!`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd przesyłania plików');
    } finally {
      setLoading(false);
    }
  };

  const handleTransformationFileUpload = async (files: FileList) => {
    if (files.length === 0) return;

    // Maksymalnie 2 zdjęcia dla transformacji
    if (transformationFormData.images.length + files.length > 2) {
      setError('Transformacje mogą mieć maksymalnie 2 zdjęcia');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('images', file);
      });

      const response = await api.uploadImages(formData);
      const uploadedFiles: UploadedFile[] = response.data.files;

      // Dodaj przesłane zdjęcia do transformationFormData.images
      const newImages = uploadedFiles.map(file => file.original);
      
      setTransformationFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages]
      }));

      setSuccess(`Przesłano ${uploadedFiles.length} plików do transformacji pomyślnie!`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd przesyłania plików');
    } finally {
      setLoading(false);
    }
  };

  // Gallery handlers
  const handleGallerySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!galleryFormData.title || galleryFormData.images.length === 0) {
      setError('Wypełnij wymagane pola: tytuł i dodaj przynajmniej jedno zdjęcie');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingGalleryItem) {
        await api.updateGalleryItem(editingGalleryItem.id, galleryFormData);
        setSuccess('Projekt zaktualizowany pomyślnie!');
      } else {
        await api.addGalleryItem(galleryFormData);
        setSuccess('Projekt dodany pomyślnie!');
      }
      
      loadData();
      resetGalleryForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd zapisywania projektu');
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryEdit = (item: GalleryItem) => {
    setEditingGalleryItem(item);
    setGalleryFormData({
      title: item.title,
      description: item.description,
      category: item.category || '',
      location: item.location || '',
      images: item.images || [],
    });
  };

  const handleGalleryDelete = async (id: number) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten projekt?')) return;

    setLoading(true);
    setError(null);

    try {
      await api.deleteGalleryItem(id);
      setSuccess('Projekt usunięty pomyślnie!');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd usuwania projektu');
    } finally {
      setLoading(false);
    }
  };

  const resetGalleryForm = () => {
    setGalleryFormData({
      title: '',
      description: '',
      category: '',
      location: '',
      images: [],
    });
    setEditingGalleryItem(null);
  };

  // Transformation handlers
  const handleTransformationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!transformationFormData.title || transformationFormData.images.length < 2) {
      setError('Wypełnij wymagane pola: tytuł i dodaj przynajmniej 2 zdjęcia (przed i po)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Przekonwertuj nową strukturę na starą dla kompatybilności
      const transformationData = {
        title: transformationFormData.title,
        description: transformationFormData.description,
        category: transformationFormData.category,
        location: transformationFormData.location,
        beforeImage: transformationFormData.images[transformationFormData.beforeImageIndex],
        afterImage: transformationFormData.images[transformationFormData.afterImageIndex],
      };

      if (editingTransformation) {
        await transformationService.updateTransformation(editingTransformation.id, transformationData);
        setSuccess('Transformacja zaktualizowana pomyślnie!');
      } else {
        await transformationService.addTransformation(transformationData);
        setSuccess('Transformacja dodana pomyślnie!');
      }
      
      loadData();
      resetTransformationForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd zapisywania transformacji');
    } finally {
      setLoading(false);
    }
  };

  const handleTransformationEdit = (item: TransformationItem) => {
    setEditingTransformation(item);
    setTransformationFormData({
      title: item.title,
      description: item.description,
      category: item.category || '',
      location: item.location || '',
      images: item.beforeImage && item.afterImage ? [item.beforeImage, item.afterImage] : [],
      beforeImageIndex: 0,
      afterImageIndex: 1,
    });
  };

  const handleTransformationDelete = async (id: number) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę transformację?')) return;

    setLoading(true);
    setError(null);

    try {
      await transformationService.deleteTransformation(id);
      setSuccess('Transformacja usunięta pomyślnie!');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd usuwania transformacji');
    } finally {
      setLoading(false);
    }
  };

  const resetTransformationForm = () => {
    setTransformationFormData({
      title: '',
      description: '',
      category: '',
      location: '',
      images: [],
      beforeImageIndex: 0,
      afterImageIndex: 1,
    });
    setEditingTransformation(null);
  };

  // Content handlers
  const handleContentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!siteContent) return;

    setLoading(true);
    setError(null);

    try {
      await contentService.updateContent(siteContent);
      setSuccess('Treść strony zaktualizowana pomyślnie!');
      setEditingContent(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd aktualizacji treści');
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md"
        >
          <h1 className="text-2xl font-bold text-center mb-6">
            Panel Administracyjny
          </h1>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Login
              </label>
              <input
                type="text"
                value={loginData.login}
                onChange={(e) => setLoginData({...loginData, login: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hasło
              </label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Logowanie...' : 'Zaloguj się'}
            </button>
          </form>
          
          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Panel Administracyjny Aluxim
          </h1>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Wyloguj
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Messages */}
        {(error || success) && (
          <div className="mb-6">
            {error && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between items-center">
                <span>{error}</span>
                <button onClick={clearMessages} className="text-red-700 hover:text-red-900">
                  ×
                </button>
              </div>
            )}
            {success && (
              <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex justify-between items-center">
                <span>{success}</span>
                <button onClick={clearMessages} className="text-green-700 hover:text-green-900">
                  ×
                </button>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow">
            <div className="border-b border-gray-200">
              <div className="px-6">
                <nav className="flex flex-wrap gap-4 overflow-x-auto">
                  <button
                    onClick={() => setActiveTab('gallery')}
                    className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === 'gallery'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    📸 Galeria Projektów
                  </button>
                  <button
                    onClick={() => setActiveTab('transformations')}
                    className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === 'transformations'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    🔄 Transformacje
                  </button>
                  <button
                    onClick={() => setActiveTab('content')}
                    className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === 'content'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    📝 Treść Strony
                  </button>
                  <button
                    onClick={() => setActiveTab('users')}
                    className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === 'users'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    👥 Użytkownicy
                  </button>
                  <button
                    onClick={() => setActiveTab('analytics')}
                    className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === 'analytics'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    📊 Statystyki
                  </button>
                  <button
                    onClick={() => setActiveTab('settings')}
                    className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === 'settings'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    ⚙️ Ustawienia
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'gallery' && (
          <GalleryTab
            formData={galleryFormData}
            setFormData={setGalleryFormData}
            editingItem={editingGalleryItem}
            handleSubmit={handleGallerySubmit}
            handleFileUpload={handleGalleryFileUpload}
            resetForm={resetGalleryForm}
            items={galleryItems}
            handleEdit={handleGalleryEdit}
            handleDelete={handleGalleryDelete}
            loading={loading}
          />
        )}

        {activeTab === 'transformations' && (
          <TransformationsTab
            formData={transformationFormData}
            setFormData={setTransformationFormData}
            editingItem={editingTransformation}
            handleSubmit={handleTransformationSubmit}
            handleFileUpload={handleTransformationFileUpload}
            resetForm={resetTransformationForm}
            items={transformations}
            handleEdit={handleTransformationEdit}
            handleDelete={handleTransformationDelete}
            loading={loading}
          />
        )}

        {activeTab === 'content' && (
          <div>
            {siteContent ? (
              <ContentTab
                content={siteContent}
                setContent={setSiteContent}
                editingContent={editingContent}
                setEditingContent={setEditingContent}
                handleSubmit={handleContentSubmit}
                handleFileUpload={handleFileUpload}
                loading={loading}
              />
            ) : (
              <div className="bg-white p-6 rounded-lg shadow text-center">
                <p>Ładowanie danych treści...</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <UsersTab />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab />
        )}

        {activeTab === 'settings' && (
          <SettingsTab />
        )}
      </div>
    </div>
  );
};

// Gallery Tab Component
const GalleryTab: React.FC<{
  formData: any;
  setFormData: (data: any) => void;
  editingItem: any;
  handleSubmit: (e: React.FormEvent) => void;
  handleFileUpload: (files: FileList) => void;
  resetForm: () => void;
  items: any[];
  handleEdit: (item: any) => void;
  handleDelete: (id: number) => void;
  loading: boolean;
}> = ({ formData, setFormData, editingItem, handleSubmit, handleFileUpload, resetForm, items, handleEdit, handleDelete, loading }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Form */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">
          {editingItem ? 'Edytuj projekt' : 'Dodaj nowy projekt do galerii'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tytuł *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opis
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategoria
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="np. Ogród zimowy"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lokalizacja
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="np. Poznań"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Zdjęcia galerii
            </label>
            
            {/* Display current images */}
            {formData.images && formData.images.length > 0 && (
              <div className="mb-4">
                <div className="grid grid-cols-2 gap-2">
                  {formData.images.map((imageUrl: string, index: number) => (
                    <div key={index} className="relative">
                      <img 
                        src={imageUrl} 
                        alt={`Zdjęcie ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newImages = formData.images.filter((_: any, i: number) => i !== index);
                          setFormData({...formData, images: newImages});
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Upload new images */}
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              Wybierz obrazy do przesłania (max 10MB każdy). Możesz dodać kilka zdjęć.
            </p>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Zapisywanie...' : editingItem ? 'Zaktualizuj' : 'Dodaj do galerii'}
            </button>
            
            {editingItem && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Anuluj
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Items List */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">
          Projekty w galerii ({items.length})
        </h2>
        
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {items.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-gray-900">{item.title}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Edytuj
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Usuń
                  </button>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm mb-2">{item.description}</p>
              
              {/* Display images */}
              {item.images && item.images.length > 0 && (
                <div className="mb-2">
                  <div className="flex gap-1 flex-wrap">
                    {item.images.slice(0, 4).map((imageUrl: string, index: number) => (
                      <img 
                        key={index}
                        src={imageUrl} 
                        alt={`Zdjęcie ${index + 1}`}
                        className="w-12 h-12 object-cover rounded border border-gray-300"
                      />
                    ))}
                    {item.images.length > 4 && (
                      <div className="w-12 h-12 bg-gray-100 rounded border border-gray-300 flex items-center justify-center text-xs text-gray-500">
                        +{item.images.length - 4}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 text-xs text-gray-500">
                {item.category && (
                  <span className="bg-gray-100 px-2 py-1 rounded">{item.category}</span>
                )}
                {item.location && (
                  <span className="bg-gray-100 px-2 py-1 rounded">{item.location}</span>
                )}
              </div>
            </div>
          ))}
          
          {items.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              Brak projektów w galerii
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Transformations Tab Component
const TransformationsTab: React.FC<{
  formData: any;
  setFormData: (data: any) => void;
  editingItem: any;
  handleSubmit: (e: React.FormEvent) => void;
  handleFileUpload: (files: FileList) => void;
  resetForm: () => void;
  items: any[];
  handleEdit: (item: any) => void;
  handleDelete: (id: number) => void;
  loading: boolean;
}> = ({ formData, setFormData, editingItem, handleSubmit, handleFileUpload, resetForm, items, handleEdit, handleDelete, loading }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Form */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">
          {editingItem ? 'Edytuj transformację' : 'Dodaj nową transformację przed/po'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tytuł transformacji *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="np. Ogród zimowy - transformacja"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opis transformacji
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Opisz proces transformacji, użyte materiały, efekty..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategoria
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Wybierz kategorię</option>
                <option value="Ogród zimowy">Ogród zimowy</option>
                <option value="Zabudowa tarasu">Zabudowa tarasu</option>
                <option value="Przeszklenie balkonu">Przeszklenie balkonu</option>
                <option value="System bezramowy">System bezramowy</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lokalizacja
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="np. Poznań"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Zdjęcia transformacji (maksymalnie 2)
            </label>
            
            {/* Display current images */}
            {formData.images && formData.images.length > 0 && (
              <div className="mb-4">
                <div className="grid grid-cols-2 gap-4">
                  {formData.images.map((imageUrl: string, index: number) => (
                    <div key={index} className="relative">
                      <div className="relative">
                        <img 
                          src={imageUrl} 
                          alt={`Zdjęcie ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newImages = formData.images.filter((_: any, i: number) => i !== index);
                            setFormData({...formData, images: newImages});
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                      
                      <div className="mt-2">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="beforeImage"
                            checked={formData.beforeImageIndex === index}
                            onChange={() => setFormData({...formData, beforeImageIndex: index})}
                            className="text-blue-600"
                          />
                          <span className="text-sm">To jest zdjęcie PRZED</span>
                        </label>
                        <label className="flex items-center space-x-2 mt-1">
                          <input
                            type="radio"
                            name="afterImage"
                            checked={formData.afterImageIndex === index}
                            onChange={() => setFormData({...formData, afterImageIndex: index})}
                            className="text-green-600"
                          />
                          <span className="text-sm">To jest zdjęcie PO</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Upload new images - tylko jeśli jest mniej niż 2 */}
            {formData.images.length < 2 && (
              <>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Dodaj {2 - formData.images.length} zdjęć do transformacji (max 10MB każdy). Następnie wybierz które jest przed, a które po.
                </p>
              </>
            )}
            
            {formData.images.length >= 2 && (
              <p className="text-sm text-green-600 mt-1">
                ✓ Dodano maksymalną liczbę zdjęć. Wybierz które jest przed, a które po transformacji.
              </p>
            )}
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Zapisywanie...' : editingItem ? 'Zaktualizuj transformację' : 'Dodaj transformację'}
            </button>
            
            {editingItem && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Anuluj
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Transformations List */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">
          Transformacje przed/po ({items.length})
        </h2>
        
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {items.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-gray-900">{item.title}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Edytuj
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Usuń
                  </button>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm mb-2">{item.description}</p>
              
              <div className="flex gap-2 text-xs text-gray-500 mb-3">
                {item.category && (
                  <span className="bg-gray-100 px-2 py-1 rounded">{item.category}</span>
                )}
                {item.location && (
                  <span className="bg-gray-100 px-2 py-1 rounded">{item.location}</span>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">PRZED</p>
                  <img
                    src={item.images && item.beforeImageIndex !== undefined 
                      ? item.images[item.beforeImageIndex] 
                      : item.beforeImage}
                    alt="Przed transformacją"
                    className="w-full h-24 object-cover rounded border"
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">PO</p>
                  <img
                    src={item.images && item.afterImageIndex !== undefined 
                      ? item.images[item.afterImageIndex] 
                      : item.afterImage}
                    alt="Po transformacji"
                    className="w-full h-24 object-cover rounded border"
                  />
                </div>
              </div>
            </div>
          ))}
          
          {items.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              Brak transformacji
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Content Tab Component
const ContentTab: React.FC<{
  content: SiteContent;
  setContent: (content: SiteContent) => void;
  editingContent: boolean;
  setEditingContent: (editing: boolean) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleFileUpload: (files: FileList) => void;
  loading: boolean;
}> = ({ content, setContent, editingContent, setEditingContent, handleSubmit, handleFileUpload, loading }) => {
  const updateContent = (section: keyof SiteContent, field: string, value: string) => {
    setContent({
      ...content,
      [section]: {
        ...content[section],
        [field]: value
      }
    });
  };

  const updateServiceItem = (index: number, field: string, value: string) => {
    const newItems = [...content.services.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    setContent({
      ...content,
      services: {
        ...content.services,
        items: newItems
      }
    });
  };

  const addServiceItem = () => {
    setContent({
      ...content,
      services: {
        ...content.services,
        items: [
          ...content.services.items,
          { title: '', description: '', icon: '' }
        ]
      }
    });
  };

  const removeServiceItem = (index: number) => {
    const newItems = content.services.items.filter((_, i) => i !== index);
    setContent({
      ...content,
      services: {
        ...content.services,
        items: newItems
      }
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Zarządzanie treścią strony</h2>
          <div className="flex gap-4">
            {editingContent ? (
              <>
                <button
                  onClick={() => setEditingContent(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Zapisywanie...' : 'Zapisz zmiany'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditingContent(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Edytuj treść
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Upload zdjęć */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Upload zdjęć</h3>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-sm text-gray-500 mt-1">
          Prześlij zdjęcia do użycia na stronie (max 10MB każdy)
        </p>
      </div>

      {/* Hero Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Sekcja Hero (Główna)</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tytuł główny
            </label>
            <input
              type="text"
              value={content.hero.title}
              onChange={(e) => updateContent('hero', 'title', e.target.value)}
              disabled={!editingContent}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Podtytuł
            </label>
            <textarea
              value={content.hero.subtitle}
              onChange={(e) => updateContent('hero', 'subtitle', e.target.value)}
              disabled={!editingContent}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              rows={3}
            />
          </div>
          {editingContent && (
            <ImageUploadComponent
              onImageUploaded={(imageUrl) => updateContent('hero', 'backgroundImage', imageUrl)}
              currentImageUrl={content.hero.backgroundImage}
              label="Zdjęcie tła (opcjonalne)"
              className="mt-4"
            />
          )}
        </div>
      </div>

      {/* About Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Sekcja O Nas</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tytuł
            </label>
            <input
              type="text"
              value={content.about.title}
              onChange={(e) => updateContent('about', 'title', e.target.value)}
              disabled={!editingContent}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Treść
            </label>
            <textarea
              value={content.about.content}
              onChange={(e) => updateContent('about', 'content', e.target.value)}
              disabled={!editingContent}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              rows={4}
            />
          </div>
          
          {editingContent && (
            <ImageUploadComponent
              onImageUploaded={(imageUrl) => updateContent('about', 'image', imageUrl)}
              currentImageUrl={content.about.image}
              label="Zdjęcie sekcji O Nas (opcjonalne)"
              className="mt-4"
            />
          )}
        </div>
      </div>

      {/* Services Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Sekcja Usługi</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tytuł sekcji
            </label>
            <input
              type="text"
              value={content.services.title}
              onChange={(e) => updateContent('services', 'title', e.target.value)}
              disabled={!editingContent}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Usługi
              </label>
              {editingContent && (
                <button
                  onClick={addServiceItem}
                  className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  Dodaj usługę
                </button>
              )}
            </div>
            
            <div className="space-y-4">
              {content.services.items.map((service, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-900">Usługa {index + 1}</h4>
                    {editingContent && (
                      <button
                        onClick={() => removeServiceItem(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Usuń
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Tytuł</label>
                      <input
                        type="text"
                        value={service.title}
                        onChange={(e) => updateServiceItem(index, 'title', e.target.value)}
                        disabled={!editingContent}
                        className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Opis</label>
                      <textarea
                        value={service.description}
                        onChange={(e) => updateServiceItem(index, 'description', e.target.value)}
                        disabled={!editingContent}
                        className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Ikona (emoji)</label>
                      <input
                        type="text"
                        value={service.icon}
                        onChange={(e) => updateServiceItem(index, 'icon', e.target.value)}
                        disabled={!editingContent}
                        className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        placeholder="🏠"
                      />
                    </div>
                  </div>
                  
                  {editingContent && (
                    <div className="mt-4">
                      <ImageUploadComponent
                        onImageUploaded={(imageUrl) => updateServiceItem(index, 'image', imageUrl)}
                        currentImageUrl={service.image}
                        label="Zdjęcie usługi (opcjonalne)"
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Sekcja Kontakt</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tytuł
            </label>
            <input
              type="text"
              value={content.contact.title}
              onChange={(e) => updateContent('contact', 'title', e.target.value)}
              disabled={!editingContent}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefon 1
              </label>
              <input
                type="text"
                value={content.contact.phone}
                onChange={(e) => updateContent('contact', 'phone', e.target.value)}
                disabled={!editingContent}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefon 2
              </label>
              <input
                type="text"
                value={content.contact.phone2 || ''}
                onChange={(e) => updateContent('contact', 'phone2', e.target.value)}
                disabled={!editingContent}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={content.contact.email}
                onChange={(e) => updateContent('contact', 'email', e.target.value)}
                disabled={!editingContent}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Siedziba
              </label>
              <input
                type="text"
                value={content.contact.address}
                onChange={(e) => updateContent('contact', 'address', e.target.value)}
                disabled={!editingContent}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warsztat
              </label>
              <input
                type="text"
                value={content.contact.workshop || ''}
                onChange={(e) => updateContent('contact', 'workshop', e.target.value)}
                disabled={!editingContent}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Sekcja Footer</h3>
            <p className="text-sm text-gray-500">Zarządzaj treścią w stopce strony</p>
          </div>
        </div>

        <div className="space-y-6">
          {editingContent && (
            <ImageUploadComponent
              onImageUploaded={(imageUrl) => setContent({
                ...content,
                footer: { ...content.footer, logo: imageUrl }
              })}
              currentImageUrl={content.footer.logo}
              label="Logo firmy (opcjonalne)"
              className="mb-6"
            />
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nazwa firmy
              </label>
              <input
                type="text"
                value={content.footer.companyName}
                onChange={(e) => updateContent('footer', 'companyName', e.target.value)}
                disabled={!editingContent}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Opis firmy
              </label>
              <textarea
                value={content.footer.description}
                onChange={(e) => updateContent('footer', 'description', e.target.value)}
                disabled={!editingContent}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link do Facebook
              </label>
              <input
                type="url"
                value={content.footer.socialLinks.facebook}
                onChange={(e) => setContent({
                  ...content,
                  footer: {
                    ...content.footer,
                    socialLinks: {
                      ...content.footer.socialLinks,
                      facebook: e.target.value
                    }
                  }
                })}
                disabled={!editingContent}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link do Instagram
              </label>
              <input
                type="url"
                value={content.footer.socialLinks.instagram}
                onChange={(e) => setContent({
                  ...content,
                  footer: {
                    ...content.footer,
                    socialLinks: {
                      ...content.footer.socialLinks,
                      instagram: e.target.value
                    }
                  }
                })}
                disabled={!editingContent}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>

          {/* Footer Sections */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Sekcje w stopce</h4>
            {content.footer.sections.map((section, index) => (
              <div key={index} className="mb-4 p-4 border rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) => {
                      const newSections = [...content.footer.sections];
                      newSections[index] = { ...newSections[index], title: e.target.value };
                      setContent({
                        ...content,
                        footer: { ...content.footer, sections: newSections }
                      });
                    }}
                    disabled={!editingContent}
                    placeholder="Tytuł sekcji"
                    className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newSections = content.footer.sections.filter((_, i) => i !== index);
                      setContent({
                        ...content,
                        footer: { ...content.footer, sections: newSections }
                      });
                    }}
                    disabled={!editingContent}
                    className="ml-2 px-3 py-1 text-red-600 hover:text-red-800 disabled:text-gray-400"
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-2">
                  {section.links.map((link, linkIndex) => (
                    <div key={linkIndex} className="flex gap-2">
                      <input
                        type="text"
                        value={link}
                        onChange={(e) => {
                          const newSections = [...content.footer.sections];
                          const newLinks = [...newSections[index].links];
                          newLinks[linkIndex] = e.target.value;
                          newSections[index] = { ...newSections[index], links: newLinks };
                          setContent({
                            ...content,
                            footer: { ...content.footer, sections: newSections }
                          });
                        }}
                        disabled={!editingContent}
                        placeholder="Link"
                        className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newSections = [...content.footer.sections];
                          const newLinks = newSections[index].links.filter((_, i) => i !== linkIndex);
                          newSections[index] = { ...newSections[index], links: newLinks };
                          setContent({
                            ...content,
                            footer: { ...content.footer, sections: newSections }
                          });
                        }}
                        disabled={!editingContent}
                        className="px-3 py-1 text-red-600 hover:text-red-800 disabled:text-gray-400"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newSections = [...content.footer.sections];
                      newSections[index] = {
                        ...newSections[index],
                        links: [...newSections[index].links, '']
                      };
                      setContent({
                        ...content,
                        footer: { ...content.footer, sections: newSections }
                      });
                    }}
                    disabled={!editingContent}
                    className="px-3 py-1 text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                  >
                    + Dodaj link
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                setContent({
                  ...content,
                  footer: {
                    ...content.footer,
                    sections: [...content.footer.sections, { title: '', links: [''] }]
                  }
                });
              }}
              disabled={!editingContent}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            >
              + Dodaj sekcję
            </button>
          </div>
        </div>
      </div>

      {/* SEO Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">SEO</h3>
            <p className="text-sm text-gray-500">Zarządzaj ustawieniami SEO strony</p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tytuł strony (SEO)
            </label>
            <input
              type="text"
              value={content.seo.title}
              onChange={(e) => updateContent('seo', 'title', e.target.value)}
              disabled={!editingContent}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opis strony (SEO)
            </label>
            <textarea
              value={content.seo.description}
              onChange={(e) => updateContent('seo', 'description', e.target.value)}
              disabled={!editingContent}
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Słowa kluczowe (SEO)
            </label>
            <input
              type="text"
              value={content.seo.keywords}
              onChange={(e) => updateContent('seo', 'keywords', e.target.value)}
              disabled={!editingContent}
              placeholder="słowo1, słowo2, słowo3"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Zdjęcie OpenGraph
            </label>
            {editingContent ? (
              <ImageUploadComponent
                onImageUploaded={(imageUrl) => updateContent('seo', 'ogImage', imageUrl)}
                currentImageUrl={content.seo.ogImage}
                label="Zdjęcie OpenGraph (meta tag)"
                className="mt-2"
              />
            ) : (
              <input
                type="url"
                value={content.seo.ogImage}
                disabled={true}
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Users Tab Component
const UsersTab: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-4">👥 Zarządzanie Użytkownikami</h3>
      <div className="space-y-4">
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Aktualny użytkownik</h4>
          <p className="text-gray-600">Administrator systemu</p>
          <p className="text-sm text-gray-500">Ostatnie logowanie: dzisiaj</p>
        </div>
        
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Zmiana hasła</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="password"
              placeholder="Nowe hasło"
              className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="password"
              placeholder="Powtórz hasło"
              className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button className="mt-3 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition">
            Zmień hasło
          </button>
        </div>
      </div>
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">📊 Statystyki Strony</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900">Projekty w galerii</h4>
            <p className="text-2xl font-bold text-blue-600">12</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-medium text-green-900">Transformacje</h4>
            <p className="text-2xl font-bold text-green-600">8</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-medium text-purple-900">Kategorie</h4>
            <p className="text-2xl font-bold text-purple-600">5</p>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Ostatnie aktualizacje</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>• Dodano nowy projekt w galerii - 2 dni temu</li>
            <li>• Zaktualizowano treść strony "O nas" - 5 dni temu</li>
            <li>• Dodano transformację "Ogród zimowy" - 1 tydzień temu</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// Settings Tab Component
const SettingsTab: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">⚙️ Ustawienia Systemu</h3>
        
        <div className="space-y-6">
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Ustawienia witryny</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nazwa witryny
                </label>
                <input
                  type="text"
                  defaultValue="ALUXIM"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email kontaktowy
                </label>
                <input
                  type="email"
                  defaultValue="kontakt@aluxim.pl"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Backup i przywracanie</h4>
            <div className="flex space-x-4">
              <button className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition">
                📥 Utwórz backup
              </button>
              <button className="bg-yellow-600 text-white py-2 px-4 rounded-lg hover:bg-yellow-700 transition">
                📤 Przywróć z backup
              </button>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Bezpieczeństwo</h4>
            <div className="space-y-3">
              <label className="flex items-center">
                <input type="checkbox" className="mr-2" defaultChecked />
                <span className="text-sm text-gray-700">Wymuszaj HTTPS</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="mr-2" defaultChecked />
                <span className="text-sm text-gray-700">Logowanie nieudanych prób logowania</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="mr-2" />
                <span className="text-sm text-gray-700">Ograniczenie prób logowania</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
